import React, { FC, Fragment, ReactNode, useEffect, useState } from 'react';
import {
  BaseModal,
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Input
} from '@cokitchen/cokitchen-components';
import { AddIcon, ArrowDownIcon } from 'cokitchen-icons';
import { apiService } from 'utils/api';
import { IssueType } from 'models/issue.model';
import moment from 'moment';
import { getUserName } from 'utils/functions';

type ReviewModalProps = {
  show: boolean;
  type?: 'resolved' | 'unresolved';
  children?: ReactNode;
  onClose: (refetchData?: boolean) => void;
  issue: IssueType;
  selectedCsRep: selectedRep;
  setSelectedCsRep: (selectedCsRep: selectedRep) => void;
  resolution: string;
  setResolution: (resolution: string) => void;
  setShowMarkModal: (showMarkModal: boolean) => void;
};

type selectedRep = {
  name: string;
  id: string;
};

type ReviewModalFooterProps = {
  onCancel: () => void;
  onConfirm: () => void;
  type?: 'resolved' | 'unresolved';
  disabled: boolean;
};

const UnResolveFooterProps: FC<ReviewModalFooterProps> = ({ onConfirm, disabled }) => (
  <div>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      disabled={disabled}
      onClick={onConfirm}>
      Mark as Resolved
    </Button>
  </div>
);

const ReviewModal = ({
  show,
  onClose,
  type = 'resolved',
  issue,
  resolution,
  setResolution,
  selectedCsRep,
  setSelectedCsRep,
  setShowMarkModal
}: ReviewModalProps) => {
  const [csRepList, setCsRepList] = useState<selectedRep[]>([]);
  const [csRepLoading, setCsRepLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const customer_name = getUserName(issue.user);
  const cs_rep = getUserName(issue.assigned_rep);

  const onConfirm = () => {
    setShowMarkModal(true);
    onClose();
  };

  const getAllReps = async () => {
    setCsRepLoading(true);
    try {
      const res = await apiService.get('cs/staff/all', {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const allCsRep = res.data.data.map((item: any) => {
        return {
          name: getUserName(item),
          id: item.id
        };
      });
      setCsRepList(allCsRep);
    } finally {
      setCsRepLoading(false);
    }
  };
  useEffect(() => {
    getAllReps();
  }, []);

  return (
    <BaseModal
      bodyOpenClassName='hide-modal-icon'
      isOpen={show}
      title={``}
      titleIcon={<AddIcon className='text-blue-5' />}
      titleIconColor='info'
      onRequestClose={() => onClose()}
      footer={
        type === 'unresolved' ? (
          <UnResolveFooterProps
            onCancel={() => onClose()}
            onConfirm={onConfirm}
            disabled={!selectedCsRep || !resolution}
          />
        ) : undefined
      }>
      <div className='absolute left-44 top-8'>
        <div className='flex gap-[7px] items-center'>
          <p className='font-semibold text-[#1A1A1A] uppercase text-[20px]'>#{issue.order_code}</p>
          <p
            className={`text-xs border px-[10px] rounded-[40px] ${
              type == 'resolved'
                ? 'border-green-4 text-green-3 bg-[#e7feed]'
                : 'border-red-4 text-red-3 bg-[#FEE7EA]'
            }`}>
            {type}
          </p>
        </div>
      </div>

      <div className=' text-[14px text-[#1A1A1A] px-3 pt-5'>
        <div className='flex justify-between border-[#EAEBEB] border-b pb-[7px]'>
          <p className='text-[#858585]'>Name</p>
          <p>{customer_name}</p>
        </div>
        {type !== 'resolved' && (
          <div className='flex justify-between border-[#EAEBEB] items-center border-b py-[20px]'>
            <p className='text-[#858585]'>CS Rep Assigned</p>

            <div className='border-[#EAEBEB]'>
              <p>{cs_rep}</p>
            </div>
          </div>
        )}

        <div className='flex justify-between border-[#EAEBEB] border-b py-[20px]'>
          <p className='text-[#858585]'>Issue</p>
          <p className='w-[75%] text-right'>{issue.feedback_summary}</p>
        </div>

        <div className='flex justify-between border-[#EAEBEB] border-b py-[20px]'>
          <p className='text-[#858585]'>Category</p>
          <p>{issue.feedback_destination}</p>
        </div>

        {type !== 'resolved' ? (
          <Fragment>
            <div className='flex justify-between items-center border-[#EAEBEB] border-b py-[20px]'>
              <p className='text-[#858585]'>Resolved by</p>

              <Dropdown
                placement='bottom-start'
                visible={showDropdown}
                onClickOutside={() => setShowDropdown(false)}
                content={
                  <div
                    className='px-5 py-3 flex items-center justify-between text-sm gap-3 font-medium w-full border-[1px] border-[#E0DADB] rounded-lg'
                    onClick={() => setShowDropdown(!showDropdown)}>
                    <span>{selectedCsRep.name ? selectedCsRep.name : 'Select CS Rep'}</span>
                    <ArrowDownIcon />
                  </div>
                }>
                <DropdownList>
                  {csRepLoading ? (
                    <DropdownItem className='text-center'>Loading...</DropdownItem>
                  ) : (
                    csRepList.map((item, index) => (
                      <DropdownItem
                        key={index}
                        onClick={() => {
                          setSelectedCsRep(item);
                          setShowDropdown(false);
                        }}>
                        {item.name}
                      </DropdownItem>
                    ))
                  )}
                </DropdownList>
              </Dropdown>
            </div>

            <div className='border-[#EAEBEB] border-b py-[20px] flex justify-between items-center'>
              <p className='text-[#858585]'>Resolution</p>

              <div>
                <Input
                  name='resolution'
                  small
                  inputClass='placeholder:text-sm'
                  className='text-[14px] '
                  placeholder='How it was resolved'
                  onChange={(e) => setResolution(e.target.value)}
                  value={resolution}
                />
              </div>
            </div>
          </Fragment>
        ) : (
          <div className='justify-between border-[#EAEBEB] border-b py-[20px]'>
            <p className='text-[#858585]'>Resolution</p>
            <p className='w-[75%] text-right'>{issue.resolution}</p>
          </div>
        )}

        <div className='flex justify-between py-[20px]'>
          <p className='text-[#858585]'>Date </p>

          <div className='flex items-center gap-2'>
            <p>{issue.created_at?.split('T')[0]}</p>
            <div className='bg-gray-2 min-w-[8px] w-2 h-2 rounded-full'></div>
            <p> {moment(issue.created_at).format('hh:mm a')} </p>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default ReviewModal;
